/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * System Agents API Client
 * 
 * This module provides helper functions for calling the system agents APIs
 * for managing runs, findings, and schedules.
 * 
 * API Structure:
 * - /api/agents?category=system_* - List system agents (existing agents endpoint with filter)
 * - /api/agents/:id - Get agent details (existing agents endpoint)
 * - /api/agents/system/runs - Run management
 * - /api/agents/system/findings - Findings management
 * - /api/agents/system/schedules - Schedule management
 * - /api/agents/system/:id/runs - Agent-specific run operations
 */

import type {
  SystemAgentRun,
  SystemAgentFinding,
  SystemAgentSchedule,
  FindingsSummary,
  SystemAgentRunWithAgent,
  ListSystemAgentRunsOptions,
  ListSystemAgentFindingsOptions,
  FindingSeverity,
  FindingStatus,
  SystemAgentRunStatus,
  TriggerSource,
} from '@shared/schema/systemAgents';
import type { Agent } from '@shared/schema/agents';

// Base URL for main SkillShop server (where system agents API lives)
const getBaseUrl = () => {
  // System agents API is on the main SkillShop server (port 5173)
  return 'http://localhost:5173';
};

// ============================================================================
// System Agents (via existing agents API with category filter)
// ============================================================================

export interface ListSystemAgentsResult {
  success: boolean;
  agents?: Agent[];
  total?: number;
  error?: string;
}

/**
 * List all system agents
 * Uses the existing /api/agents endpoint with category filter for system agent categories
 */
export async function listSystemAgents(): Promise<ListSystemAgentsResult> {
  try {
    // Query agents with system categories using the existing agents endpoint
    const categories = ['system_audit', 'system_measurement', 'system_review', 'system_evaluation'];
    const params = new URLSearchParams();
    categories.forEach(cat => params.append('category', cat));
    
    const response = await fetch(`${getBaseUrl()}/api/agent/agents?${params.toString()}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch system agents' };
    }
    
    return {
      success: true,
      agents: data.data?.agents || data.agents || [],
      total: data.data?.total || data.total || 0,
    };
  } catch (error: any) {
    console.error('Error fetching system agents:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get a specific system agent by ID
 * Uses the existing /api/agents/:id endpoint
 */
export async function getSystemAgent(agentId: number): Promise<{ success: boolean; agent?: Agent; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agent/agents/${agentId}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch agent' };
    }
    
    return { success: true, agent: data.data || data };
  } catch (error: any) {
    console.error('Error fetching agent:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update a system agent
 * Uses the existing /api/agents/:id endpoint with PUT
 */
export async function updateSystemAgent(
  agentId: number,
  updates: {
    displayName?: string;
    description?: string;
    systemPromptTemplate?: string;
    defaultModel?: string;
    enabledTools?: string[];
    memoryEnabled?: boolean;
    avatarEnabled?: boolean;
    isPublic?: boolean;
  }
): Promise<{ success: boolean; agent?: Agent; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agent/agents/${agentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    
    if (!response.ok) {
      // Handle error - data.error might be a string or an object with code/message
      const errorMessage = typeof data.error === 'string' 
        ? data.error 
        : data.error?.message || data.message || 'Failed to update agent';
      return { success: false, error: errorMessage };
    }
    
    return { success: true, agent: data.data || data };
  } catch (error: any) {
    console.error('Error updating agent:', error);
    return { success: false, error: error.message || 'Failed to update agent' };
  }
}

// ============================================================================
// System Agent Runs
// ============================================================================

export interface ListRunsResult {
  success: boolean;
  runs?: SystemAgentRunWithAgent[];
  total?: number;
  error?: string;
}

export interface CreateRunResult {
  success: boolean;
  run?: SystemAgentRun;
  error?: string;
}

/**
 * List system agent runs with optional filters
 */
export async function listSystemAgentRuns(options?: ListSystemAgentRunsOptions): Promise<ListRunsResult> {
  try {
    const params = new URLSearchParams();
    if (options?.agentId) params.append('agentId', options.agentId.toString());
    if (options?.status) params.append('status', options.status);
    if (options?.triggeredBy) params.append('triggeredBy', options.triggeredBy);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    
    const response = await fetch(`${getBaseUrl()}/api/agents/system/runs?${params.toString()}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch runs' };
    }
    
    return {
      success: true,
      runs: data.data?.runs || data.runs || [],
      total: data.data?.total || data.total || 0,
    };
  } catch (error: any) {
    console.error('Error fetching runs:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get a specific run by ID
 */
export async function getSystemAgentRun(runId: number): Promise<{ success: boolean; run?: SystemAgentRun; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agents/system/runs/${runId}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch run' };
    }
    
    return { success: true, run: data.data || data };
  } catch (error: any) {
    console.error('Error fetching run:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Trigger a new system agent run
 */
export async function triggerSystemAgentRun(
  agentId: number,
  options?: {
    targetProducts?: string[];
    triggeredBy?: TriggerSource;
  }
): Promise<CreateRunResult> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agents/system/${agentId}/runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        targetProducts: options?.targetProducts,
        triggeredBy: options?.triggeredBy || 'manual',
      }),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to trigger run' };
    }
    
    return { success: true, run: data.data || data };
  } catch (error: any) {
    console.error('Error triggering run:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Cancel a running system agent run
 */
export async function cancelSystemAgentRun(runId: number): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agents/system/runs/${runId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'cancelled' }),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to cancel run' };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error cancelling run:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// System Agent Findings
// ============================================================================

export interface ListFindingsResult {
  success: boolean;
  findings?: SystemAgentFinding[];
  total?: number;
  error?: string;
}

export interface FindingsSummaryResult {
  success: boolean;
  summary?: FindingsSummary;
  error?: string;
}

/**
 * List system agent findings with optional filters
 * @param options - Filter options
 * @param options.productId - Product UUID (optional - omit for org-wide findings, pass product.id for product-specific findings)
 * @returns List of findings
 */
export async function listSystemAgentFindings(options?: ListSystemAgentFindingsOptions): Promise<ListFindingsResult> {
  try {
    const params = new URLSearchParams();
    if (options?.agentId) params.append('agentId', options.agentId.toString());
    if (options?.runId) params.append('runId', options.runId.toString());
    if (options?.productId) params.append('productId', options.productId);
    if (options?.severity) params.append('severity', options.severity);
    if (options?.status) params.append('status', options.status);
    if (options?.category) params.append('category', options.category);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    
    const response = await fetch(`${getBaseUrl()}/api/agents/system/findings?${params.toString()}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch findings' };
    }
    
    return {
      success: true,
      findings: data.data?.findings || data.findings || [],
      total: data.data?.total || data.total || 0,
    };
  } catch (error: any) {
    console.error('Error fetching findings:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get findings summary
 * @param options - Filter options
 * @param options.productId - Product UUID (optional - omit for org-wide findings, pass product.id for product-specific findings)
 * @returns Findings summary with counts by severity, status, etc.
 */
export async function getSystemAgentFindingsSummary(options?: {
  status?: FindingStatus;
  agentId?: number;
  productId?: string;
}): Promise<FindingsSummaryResult> {
  try {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.agentId) params.append('agentId', options.agentId.toString());
    if (options?.productId) params.append('productId', options.productId);
    
    const response = await fetch(`${getBaseUrl()}/api/agents/system/findings/summary?${params.toString()}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch findings summary' };
    }
    
    return { success: true, summary: data.data || data };
  } catch (error: any) {
    console.error('Error fetching findings summary:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update a system agent finding's status
 * @param findingId - The finding ID string (e.g., "FND-20260111-001")
 * @param updates - Updates to apply (status, dismissedReason)
 * @param productId - Product UUID (optional - omit for org-wide findings, pass product.id for product-specific findings)
 * @returns Updated finding or error
 */
export async function updateSystemAgentFinding(
  findingId: string,
  updates: {
    status?: FindingStatus;
    dismissedReason?: string;
  },
  productId?: string
): Promise<{ success: boolean; finding?: SystemAgentFinding; error?: string }> {
  try {
    const params = new URLSearchParams();
    if (productId) params.append('productId', productId);
    const queryString = params.toString();
    const url = `${getBaseUrl()}/api/agents/system/findings/${findingId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to update finding' };
    }
    
    return { success: true, finding: data.data?.finding || data.finding || data.data || data };
  } catch (error: any) {
    console.error('Error updating finding:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// System Agent Schedules
// ============================================================================

export interface ListSchedulesResult {
  success: boolean;
  schedules?: SystemAgentSchedule[];
  total?: number;
  error?: string;
}

/**
 * List system agent schedules
 */
export async function listSystemAgentSchedules(agentId?: number): Promise<ListSchedulesResult> {
  try {
    const params = new URLSearchParams();
    if (agentId) params.append('agentId', agentId.toString());
    
    const response = await fetch(`${getBaseUrl()}/api/agents/system/schedules?${params.toString()}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch schedules' };
    }
    
    // Handle different response formats: data.data (array), data.data.schedules, or data.schedules
    const schedules = Array.isArray(data.data) 
      ? data.data 
      : (data.data?.schedules || data.schedules || []);
    
    return {
      success: true,
      schedules,
      total: schedules.length,
    };
  } catch (error: any) {
    console.error('Error fetching schedules:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Create a new schedule
 */
export async function createSystemAgentSchedule(schedule: {
  agentId: number;
  cronExpression: string;
  timezone?: string;
  targetProducts?: string;
  enabled?: boolean;
}): Promise<{ success: boolean; schedule?: SystemAgentSchedule; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agents/system/schedules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(schedule),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to create schedule' };
    }
    
    return { success: true, schedule: data.data || data };
  } catch (error: any) {
    console.error('Error creating schedule:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update a schedule
 */
export async function updateSystemAgentSchedule(
  scheduleId: number,
  updates: {
    cronExpression?: string;
    timezone?: string;
    targetProducts?: string;
    enabled?: boolean;
  }
): Promise<{ success: boolean; schedule?: SystemAgentSchedule; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agents/system/schedules/${scheduleId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to update schedule' };
    }
    
    return { success: true, schedule: data.data || data };
  } catch (error: any) {
    console.error('Error updating schedule:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete a schedule
 */
export async function deleteSystemAgentSchedule(scheduleId: number): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/agents/system/schedules/${scheduleId}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to delete schedule' };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting schedule:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color for severity badge
 */
export function getSeverityColor(severity: FindingSeverity): string {
  switch (severity) {
    case 'critical': return 'bg-red-600 text-white';
    case 'high': return 'bg-orange-500 text-white';
    case 'medium': return 'bg-yellow-500 text-black';
    case 'low': return 'bg-blue-500 text-white';
    case 'info': return 'bg-gray-500 text-white';
    default: return 'bg-gray-400 text-white';
  }
}

/**
 * Get color for status badge
 */
export function getRunStatusColor(status: SystemAgentRunStatus): string {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-800';
    case 'running': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'failed': return 'bg-red-100 text-red-800';
    case 'cancelled': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-600';
  }
}

/**
 * Get color for finding status badge
 */
export function getFindingStatusColor(status: FindingStatus): string {
  switch (status) {
    case 'open': return 'bg-red-100 text-red-800';
    case 'acknowledged': return 'bg-yellow-100 text-yellow-800';
    case 'fixed': return 'bg-green-100 text-green-800';
    case 'dismissed': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-100 text-gray-600';
  }
}

/**
 * Format duration in seconds to human readable
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

/**
 * Format relative time
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}

/**
 * Get icon name for agent category
 */
export function getAgentCategoryIcon(category: string): string {
  switch (category) {
    case 'system_audit': return 'Shield';
    case 'system_measurement': return 'BarChart3';
    case 'system_review': return 'GitBranch';
    case 'system_evaluation': return 'FileText';
    default: return 'Bot';
  }
}

/**
 * Get human-readable category label
 */
export function getAgentCategoryLabel(category: string): string {
  switch (category) {
    case 'system_audit': return 'Security Audit';
    case 'system_measurement': return 'Measurement';
    case 'system_review': return 'Code Review';
    case 'system_evaluation': return 'Evaluation';
    default: return 'Agent';
  }
}

// ============================================================================
// LLM Models (for agent configuration)
// ============================================================================

import type { LLMModel } from '@shared/schema/llm';

export interface ListLLMModelsResult {
  success: boolean;
  models?: LLMModel[];
  error?: string;
}

// Re-export LLMModel for convenience
export type { LLMModel };

/**
 * List available LLM models for agent configuration
 * Returns only chat-capable models that are enabled
 */
export async function listLLMModels(): Promise<ListLLMModelsResult> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/llm/models`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch LLM models' };
    }
    
    // Filter to only enabled chat models and sort by provider
    const models = (data.data || [])
      .filter((m: LLMModel) => m.enabled && m.isChat)
      .sort((a: LLMModel, b: LLMModel) => {
        // Sort by provider (anthropic first, then openai, then vertex)
        const providerOrder: Record<string, number> = { anthropic: 0, openai: 1, vertex: 2 };
        const orderA = providerOrder[a.provider] ?? 99;
        const orderB = providerOrder[b.provider] ?? 99;
        if (orderA !== orderB) return orderA - orderB;
        // Then by name
        return a.name.localeCompare(b.name);
      });
    
    return { success: true, models };
  } catch (error: any) {
    console.error('Error fetching LLM models:', error);
    return { success: false, error: error.message };
  }
}
